/**
 * EXPERIMENT COPY — "Customize your plan", the wizard's second step.
 *
 * Production's version was a single centred screen-count spinner, with billing period on its
 * own separate step and the price only revealed at Payment. This merges the three decisions
 * that are actually one decision — period, screen count, cost — onto one page with a running
 * summary, so nobody reaches the card without knowing the number.
 *
 * Three blocks: billing period (two radio cards), the screen counter with a cost breakdown,
 * and a sticky summary on the right that carries the Continue button.
 *
 * Team-billing inputs (Team Name / Purpose) are kept from production — that flow reuses this
 * step, and dropping them would silently remove the only place to rename a team mid-update.
 */
import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Info, Loader2, Minus, Plus } from 'lucide-react';
import {
  Button,
  Input,
  Label,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@scio3/ui';
import { BILLING_INTERVAL } from '@scio3/shared-types';
import type { BillingInterval } from '@scio3/shared-types';
import { isEnterKey } from '@/lib/keyboard';
import { formatCurrencyAmount } from '../../utils/plan-helpers';
// `getFeaturesLostByDowngrade` is still read — not for a feature list any more, but to decide
// whether the plan row's heading says "Upgrade to" or "Downgrade to".

/**
 * TRYING A BIGGER SCREEN-COUNT BLOCK — flip to `false` to undo, nothing else to touch.
 *
 * Scales the three things that carry the decision on that card: its heading, the stepper's
 * number, and the total. The surrounding context (screens-in-use, license count, the "Screens"
 * unit label) deliberately does NOT scale — it is context for the decision, not the decision, and
 * growing it too would just make the whole card louder rather than making the number prominent.
 */
const BIG_COUNTER_BLOCK = true;

/** Ties the disclosure button's `aria-controls` to the region it opens. */

type Props = {
  screenCount: number;
  onScreenCountChange: (value: number) => void;
  minScreens: number;
  /** Licenses on the subscription today — the baseline "originally N" is measured against. */
  currentScreens: number;
  /** Screens actually paired right now, for the subscriber-only "N/N screens used" headline. */
  usedScreens: number;
  selectedPlan: string;
  currency: string;
  billingInterval: BillingInterval;
  onBillingIntervalChange: (v: BillingInterval) => void;
  monthlyPrice: number;
  yearlyPrice: number;
  /** Label for the summary's primary button — "Subscribe" or "Update". */
  primaryLabel: string;
  onPrimary: () => void;
  primaryDisabled?: boolean;
  /** Opens Screen Management when `-` is pressed at the floor with screens still paired. */
  onDecreaseBlocked?: () => void;
  /** Paid users only — the cancel-subscription entry the old wizard footer carried. */
  onCancelSubscription?: () => void;

  /**
   * Entered from "Update Screen Number", so the plan is not in question. Hides the Monthly/Yearly
   * cards and the plan's feature list: the period is a separate decision that re-prorates the
   * whole subscription, and a feature list is a pitch for a plan the user already has and is not
   * changing. The plan NAME stays as the summary's heading — a summary with no title is worse.
   */
  screensOnly?: boolean;
  /** The plan alias the account is on TODAY, for the "Engage → Pro Plus" change line. */
  currentPlanAlias?: string;

  /**
   * Show the order summary's "Due Today" figure on this step. A configuration screen that hides
   * the total sends people to a payment step just to find out what it costs.
   */
  showTotals?: boolean;
  /** Builds the Stripe setup intent. Called once when this step first shows totals. */
  onPreparePayment?: () => void;
  promoCode?: string | null;
  /** Resolves false when the code is rejected, so the field can say so in place. */
  onApplyPromo?: (code: string) => void | Promise<boolean>;
  onRemovePromo?: () => void;
  discountLabel?: string | null;
  /** Applied coupon's rate, for the Discount row. Stripe returns `amountOff` in CENTS. */
  discountPercentOff?: number | null;
  discountAmountOff?: number | null;

  /** Card on file, if any. Present → the payment block is a "Payment method … Change" row and
   *  the summary button charges it directly instead of submitting a Stripe form. */
  /** Optional tax ID from the card form, forwarded to the wizard for post-charge submission. */

  /** --- Prorated review, for the Due Today figure in the summary ---
   *  Only meaningful for an account with a live subscription; see `due-today.ts`. */
  hasNoActiveSubscription?: boolean;
  /** `reviewResult.total` from getUpcomingSubscriptionReview. */
  reviewTotal?: number;
  /** Prorated subtotal from the review — the figure `reviewTotal` is built from. */
  reviewSubtotal?: number | null;
  /** Prorated tax from the review, in DOLLARS. Null when unknown (no review), NOT zero. */
  reviewTax?: number | null;
  /**
   * Per-jurisdiction tax breakdown, e.g. `[{ name: 'Sales Tax - Texas(8.25%)', tax: -6726 }]`.
   * NOTE the unit: `tax` here is in CENTS, unlike the dollars on `reviewTax` above.
   */
  reviewTaxs?: Array<{ name: string; tax: number }> | null;
  /**
   * The GO-FORWARD recurring charge and its line items, straight from the review — NOT the
   * prorated one-off above. Stripe's own descriptions, so the account summary needs no
   * client-side arithmetic to itemise the plan and its add-ons.
   */
  /** `reviewResult.newMonthlySubtotal` — the go-forward subtotal, before tax. */
  nextChargeSubtotal?: number | null;
  nextChargeTotal?: number | null;
  nextChargeTax?: number | null;
  nextChargeBreakdown?: Array<{ description: string; amount: number }> | null;
  reviewLoading?: boolean;
  /** When the subscription next bills AFTER this change. Interval-aware: now + the chosen term
   *  when the billing cycle resets (interval switch, or no live subscription), the live Stripe
   *  `period_end` when it survives. See `renewalDate` in useSubscriptionWizard. */
  renewalDate?: Date | null;
  /** End of the billing period the account is on TODAY — the period the Sub total is prorated
   *  against. NOT `renewalDate`, which is the next bill AFTER this change. */
  currentPeriodEnd?: Date | null;
  /** Credit carried over from the previous purchase. */
  initialCredit?: number;
  /** Plan + addons at the current selection, i.e. what Due Today is measured against. */
  addonTotal?: number;
  /**
   * A coupon ALREADY on the Stripe customer, as opposed to one entered here. It discounts the
   * recurring price, so the card has to price it in — see `recurringTotal`.
   */
  accountDiscount?: {
    percentOff?: number | null;
    amountOff?: number | null;
    label?: string;
  } | null;

  teamMode?: boolean;
  teamName?: string;
  onTeamNameChange?: (value: string) => void;
  teamDescription?: string;
  onTeamDescriptionChange?: (value: string) => void;
};

/**
 * The count between the -/+ buttons, typeable.
 *
 * Keeps its own draft string rather than writing straight through, for two reasons: mid-typing
 * states are not valid counts (clearing the field is momentarily "", and "1" on the way to "19"
 * would reprice at 1), and every commit here re-runs the prorated review. So it commits on blur
 * and on Enter, clamping to the floor.
 *
 * Raw <input> is intentional, same as the Confirmation step's spinner: this is a composite control
 * fused between two buttons, and shadcn's <Input> brings its own border, ring and padding that
 * break the flush layout.
 */
function ScreenCountInput({
  value,
  min,
  onCommit,
}: {
  value: number;
  min: number;
  onCommit: (next: number) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(String(value));
  // Resync when -/+ or the wizard change the count from outside.
  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = () => {
    const parsed = parseInt(draft, 10);
    // 9999 matches the Confirmation step's spinner: high enough never to be a real ceiling, low
    // enough that a stray keypress cannot ask Stripe for a six-figure quantity.
    const next = Number.isNaN(parsed) ? min : Math.min(Math.max(parsed, min), 9999);
    setDraft(String(next));
    if (next !== value) onCommit(next);
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      value={draft}
      aria-label={t('Number of screens')}
      onChange={(e) => setDraft(e.target.value.replace(/\D/g, ''))}
      onBlur={commit}
      onKeyDown={(e) => {
        if (isEnterKey(e)) {
          e.preventDefault();
          commit();
        }
      }}
      className={cn(
        'bg-transparent text-center font-medium text-foreground outline-none',
        // Wide-field style: a real field — its own border, its own height, wide enough that a
        // 3-digit count is not crowding the edges. `focus-visible` rather than `focus` so the
        // ring only shows for keyboard users, per the design rules for text inputs.
        // `text-xl` (20px), one step up from `text-lg`: the count is the decision on this panel
        // and read quiet next to the period cards' prices. Deliberately not `text-2xl` — that was
        // tried, and a 32px line box in the stepper's 36px frame (set by its `icon-md` buttons)
        // left 2px of air and looked jammed. 28px fits with 4px a side and needs no box change.
        BIG_COUNTER_BLOCK ? 'w-14 text-xl' : 'w-10 text-sm',
      )}
      data-testid="wizard-screens-count"
    />
  );
}

/**
 * Track width for the Configure step when it has no summary card (`hideSummaryCard`).
 *
 * 552px is not a taste value: it is exactly what the LEFT column measured in the two-column
 * layout — `max-w-5xl` (1024px) minus the grid's `gap-8` (32px), times 5/9 for the `5fr` track.
 * Keeping it means dropping the second column does not resize the panels that stay; they sit at
 * the width they always had, centred, rather than stretching to fill the old two-column track.
 *
 * Exported because the step's TITLE rides a track declared in SubscriptionWizardDialog, and a
 * heading at `max-w-5xl` over a 552px column floats a long way left of the cards it labels.
 *
 * Widened 552 → 600 (2026-09-09). The derivation above still explains where 552 came from, but a
 * five-figure total in a currency with a symbol and a group separator no longer fits beside its
 * label: at 101 screens the Monthly card wrapped "€1,262.50 billed monthly" onto a second line
 * while Yearly stayed on one, so the two cards were different heights for no reason a reader
 * could see. 48px is the smallest bump that holds that line, and it is small enough that the
 * panels still read as the same column they were.
 */
export const ONE_COLUMN_TRACK = 'max-w-[600px]';

export function WizardStepScreens({
  screenCount,
  onScreenCountChange,
  minScreens,
  currentScreens,
  usedScreens,
  currency,
  billingInterval,
  onBillingIntervalChange,
  monthlyPrice,
  yearlyPrice,
  primaryLabel,
  onPrimary,
  primaryDisabled,
  onDecreaseBlocked,
  onCancelSubscription,
  screensOnly,
  showTotals,
  onPreparePayment,
  hasNoActiveSubscription,
  reviewLoading,
  teamMode,
  teamName,
  onTeamNameChange,
  teamDescription,
  onTeamDescriptionChange,
}: Props) {
  const { t } = useTranslation();
  /** The pill has been clicked and the field is open. */

  /**
   * Summary heading: "Engage Plan", not bare "Engage".
   *
   * The heading sits directly above a price breakdown, where a lone product name reads as a
   * section label rather than as the thing being bought. Interpolated rather than concatenated so
   * word order can differ per language, and skipped when the catalog name already ends in the
   * word — plan names come from Mongo (`plan?.name`), so "Engage Plan" is a rename away and
   * "Engage Plan Plan" is not a nice way to find out.
   */

  /* "Pro Plus plan", not "Pro Plus Plan": the plan NAME is the proper noun, "plan" is the common
     noun after it. Title-casing it made the label read as a product name that does not exist.
     The regex still tolerates a catalog name that already ends in "plan" in either case, so a
     backend-supplied "Free Plan" is not doubled up. */

  /** Whether the summary card carries a feature list under the plan name. Drives both the list
   *  itself and the rule + spacing below it, so those cannot disagree. */
  const isYearly =
    billingInterval === BILLING_INTERVAL.YEARLY ||
    billingInterval === BILLING_INTERVAL.YEARLY_FREEDEVICE;

  // Yearly entries are annual totals; show the monthly equivalent so the two cards compare.
  const yearlyMonthlyEquivalent = yearlyPrice / 12;

  // The setup intent is normally built by the transition into the Payment step; with the fields
  // inline there is no such transition, so ask for it once when this block first appears.
  const preparedRef = useRef(false);
  useEffect(() => {
    if (showTotals && !preparedRef.current) {
      preparedRef.current = true;
      onPreparePayment?.();
    }
  }, [showTotals, onPreparePayment]);

  /**
   * A TOTAL — what actually leaves the account. Comma-grouped, and it drops ".00" when the
   * amount is round, so a hundred screens bills "$16,200" rather than "$16200.00". These are the
   * four- and five-figure numbers on the step, which is where both the separator and the absent
   * cents do real work for scanning.
   *
   * `Intl` via `formatCurrencyAmount` rather than the local string concat: the grouping is what
   * was missing, and hand-rolling it here would be a second, subtly different money format in
   * one component.
   */
  /**
   * Every amount on this step, rates and totals alike: comma-grouped, always two decimals.
   *
   * `Intl` via `formatCurrencyAmount` rather than a local `${symbol}${value.toFixed(2)}`, which
   * is what this used to be and is where "$16200.00" came from — no separators. One function for
   * both kinds, so a rate and a total can never be formatted two different ways in one card.
   *
   * Cents are NEVER dropped. Trimming round amounts was tried and reverted: whether a total
   * landed round depended on the rate and the screen count, so the column changed shape as you
   * stepped it, and "$14,970" beside "$1,262.50" read as broken rather than tidy.
   */
  const money = (value: number) => formatCurrencyAmount(value, currency);
  const totalMoney = money;

  /**
   * What the card is actually charged for the chosen term — NOT `perScreen`.
   *
   * `perScreen` is a DISPLAY rate: it powers the "$13.50 / screen / month" figures, which show
   * the yearly plan's monthly equivalent so the two period cards compare like for like. Feeding
   * it to Due Today billed a yearly purchase as one discounted month (7 screens yearly showed
   * $94.50 instead of $1,134.00), i.e. the 10% annual discount applied to a monthly charge.
   *
   * `yearlyPrice` is the ANNUAL per-screen price (hence `yearlyPrice / 12` above), so the yearly
   * charge is simply that times the screen count.
   */
  const chargedPerScreen = isYearly ? yearlyPrice : monthlyPrice;

  /**
   * What the "Number of screens" row prices: the chosen TERM, not a month of it.
   *
   * On yearly this is the annual charge for the count in the stepper (5 × $162.00 = $810.00), which
   * is the figure that account is actually billed. It read the monthly equivalent before
   * (`perScreen × screenCount`, $67.50), so the one row that answers "what do these screens cost"
   * quoted a twelfth of the answer while the summary beside it said $810.00.
   *
   * The monthly equivalent still has its place — the two period cards, where the whole point is
   * comparing yearly and monthly on the same unit.
   */
  const screensRowTotal = chargedPerScreen * screenCount;
  /**
   * The struck-through comparison: the same screens billed month-to-month for the same span. Yearly
   * against yearly (12 × the monthly rate), so the two figures are the same kind of thing — against
   * the annual charge, a single month's monthly cost would understate the saving twelvefold.
   */
  const screensRowWas = isYearly ? monthlyPrice * 12 * screenCount : monthlyPrice * screenCount;

  /**
   * The derivation behind Due Today: what changed, then Sub total / credit / tax.
   *
   * A function rather than inline JSX so the disclosure has one body to reveal, on both steps,
   * with no risk of two copies of ~150 lines of money rows drifting apart.
   */

  /**
   * Three behaviours, in order of specificity:
   *   - new card inline: submit the card form;
   *   - saved card: nothing to submit, so it charges directly via `onPrimary`;
   *   - no payment on this step (team billing, free-device): `onPrimary` advances.
   * Always a plain button, never `type="submit" form={STRIPE_FORM_ID}`: this same node is reused
   * across the Customize -> Payment transition, and a native submitter reads its attributes AFTER
   * the click has already advanced the step. See STRIPE_FORM_ID.
   */
  const primaryCta = (className?: string) => (
    <Button
      className={cn('relative', className)}
      type="button"
      // Always `onPrimary` — this step advances, it never charges. It used to branch to
      // `submitStripeForm` when Stripe fields were mounted here, which was the merged one-page
      // purchase, since removed. The Payment step's footer submits that form, from the dialog.
      onClick={onPrimary}
      // Also disabled while repricing: the figures this button commits to are not on screen yet.
      // It was clickable through the whole recalculation before, which let a fast click charge
      // against a total the user never saw.
      disabled={primaryDisabled || reviewLoading}
      aria-busy={reviewLoading}
      data-testid="wizard-continue-to-payment"
    >
      {/* Spinner in place of the label, not beside it: the label names the action being committed
          to ("Subscribe", "Update"), and showing it next to a spinner reads as an action already
          in progress.
          The label stays MOUNTED and merely goes invisible, with the spinner laid over it, so the
          button keeps the width its label gives it. Swapping the label out for the spinner sized
          the button to the spinner instead: it collapsed to a square on every reprice, and since
          repricing runs on each screen-count tick the CTA visibly shrank and sprang back as the
          user held the + button down. */}
      <span className={cn(reviewLoading && 'invisible')}>{primaryLabel}</span>
      {reviewLoading && (
        <Loader2 aria-hidden="true" className="absolute inset-0 m-auto h-4 w-4 animate-spin" />
      )}
    </Button>
  );

  /**
   * Cancel Subscription — a button on the CTA's row, at the opposite end of it.
   *
   * Was a full-width bordered card of its own below the button, which made the one action on this
   * step that is hard to undo the widest target in the column. Sharing the row fixes the emphasis
   * without giving up the separation the card was there to buy: opposite ends of a full-width row
   * is a wider gap than stacking them ever gave, so a misclick still has nowhere to come from.
   *
   * Sized like the CTA rather than `sm`, because they now sit on one line and two button heights
   * on the same row reads as a mistake. `ghost` rather than `outline`: an outlined destructive
   * button drew a red box that competed with the solid primary opposite it for the eye, which is
   * backwards for the secondary of the two. With no border and no fill the label alone carries it,
   * and the hover tint is what makes it obviously pressable.
   *
   * Not on the payment step: by then the user is committing to a charge, and offering to cancel
   * next to the button that pays is a contradiction.
   */
  const cancelSubscriptionAction = (className?: string) =>
    onCancelSubscription ? (
      <Button
        variant="ghost"
        onClick={onCancelSubscription}
        data-testid="wizard-cancel-subscription-btn"
        className={cn(
          'cursor-pointer text-destructive hover:bg-destructive/10 hover:text-destructive',
          className,
        )}
      >
        {t('Cancel Subscription')}
      </Button>
    ) : null;

  const decrease = () => {
    if (screenCount <= minScreens) {
      // At the floor with screens still paired: unpairing is the only way down, so hand off
      // to Screen Management rather than silently doing nothing.
      onDecreaseBlocked?.();
      return;
    }
    onScreenCountChange(screenCount - 1);
  };

  /**
   * "$27.00/month/screen" with the AMOUNT larger and semibold — it is the number being compared
   * between the two cards, and at a uniform 12px it had no more weight than its own unit.
   *
   * `<Trans>` with a named tag rather than concatenating two `t()` calls: the rate stays ONE
   * translatable string, so a locale can move the amount relative to the unit. Attribute-less
   * tag, per the i18n rules.
   *
   * Foreground, NOT `text-primary`: green is reserved for the CTA on this step (see the period
   * card's own selection note), and a green price competed with the Continue button.
   */
  /**
   * The headline rate, alone on its line and large: it is the number being compared between the two
   * cards, and inline with its own unit at `text-base` it had no more weight than the words around
   * it. The unit moved down to `billedLine()` below, where it sits in front of the charge it
   * qualifies.
   */
  const rateLabel = (price: number) => (
    <span className="text-2xl font-semibold text-foreground">{money(price)}</span>
  );

  /**
   * "/screen/month, $3078.00 billed yearly" — the unit for the amount above, then what actually
   * leaves the account and how often.
   *
   * The TOTAL is bolded inside a muted line, because that figure is the one surprise on a yearly
   * card: both rates are quoted per month per screen so the two cards compare like for like, which
   * is exactly what hides the fact that twelve months are billed at once.
   *
   * `<Trans>` with a named tag rather than three concatenated `t()` calls: the sentence stays one
   * translatable unit, so a locale can move the total relative to the unit. Attribute-less tag,
   * per the i18n rules. Two whole keys rather than interpolating "yearly"/"monthly" into one, for
   * the same reason the rate lines have four.
   */
  const billedLine = (total: number, yearly: boolean) => (
    <Trans
      i18nKey={
        yearly
          ? '/screen per month, <total>{{price}}</total> billed yearly'
          : '/screen per month, <total>{{price}}</total> billed monthly'
      }
      values={{ price: totalMoney(total) }}
      components={{ total: <span className="font-semibold text-foreground" /> }}
    />
  );

  const periodCard = (
    interval: BillingInterval,
    label: string,
    /** A node, not a string: the amount inside it carries its own weight and size. */
    priceLabel: React.ReactNode,
    /** What actually leaves the account, and how often. A node: the total inside it is bolded. */
    billedLabel: React.ReactNode,
    savingBadge?: boolean,
  ) => {
    const active = interval === BILLING_INTERVAL.YEARLY ? isYearly : !isYearly;
    return (
      <button
        type="button"
        role="radio"
        aria-checked={active}
        onClick={() => onBillingIntervalChange(interval)}
        data-testid={`wizard-period-${interval}`}
        className={cn(
          // Flat, like the rest of this step: border only, no shadow at rest or on hover.
          // No `gap`: the three rows want two different spacings, so each owns its own margin.
          'flex cursor-pointer flex-col rounded-lg border p-4 text-left',
          // Selection is carried by a darker outline alone — no green border, no tinted fill.
          // Green is reserved for the CTA here; using it for state as well made the selected
          // card compete with the Subscribe button and read as an alert.
          'transition-colors',
          active ? 'border-foreground bg-card' : 'border-border bg-card hover:border-input',
        )}
      >
        <span className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className={cn(
              // border, not border-2: at 16px a 2px ring is most of the glyph and reads heavy.
              'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
              active ? 'border-foreground' : 'border-muted-foreground',
            )}
          >
            {active && <span className="h-2 w-2 rounded-full bg-foreground" />}
          </span>
          <span className="text-sm font-medium text-foreground">{label}</span>
          {savingBadge && (
            <span className="ml-auto rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-semibold text-primary">
              {t('Save 10%')}
            </span>
          )}
        </span>
        {/* The two price lines are one unit — a rate and what that rate actually bills — so they
            sit tight together (`gap-0.5`) and hold the label row at arm's length (`mt-3`). At a
            uniform `gap-2` all three rows were equally spaced, which grouped the rate with the
            radio it is not part of and left it looking equally related to both. */}
        <span className="mt-3 flex flex-col gap-0.5">
          {priceLabel}
          {/* `text-[11px]`, not `text-xs`, and `text-balance` behind it.
              "/screen per month, $30.00 billed monthly" measures ~236px at 12px against ~233px of
              inner card width (265px card less its `p-4`), so it cleared the line by a hair and
              dropped one word onto a second line — the worst kind of wrap, a single orphan under a
              full line. 11px buys ~17px of headroom, which fits every figure this row shows.
              No `whitespace-nowrap`: a longer currency format could still exceed the width, and a
              wrap is recoverable where an overflow is not. `text-balance` is the safety net — if it
              ever does wrap, the two lines come out even instead of leaving one word alone. */}
          <span className="text-balance text-[11px] text-muted-foreground">{billedLabel}</span>
        </span>
      </button>
    );
  };

  return (
    <div className={cn('mx-auto', ONE_COLUMN_TRACK)} data-testid="wizard-step-screens">
      {/* Proportional columns (3:2), not a fixed/min-max summary track. A `minmax()` track
          claims its max before the `1fr` track gets anything, so as the window narrowed the
          summary stayed wide and the left column took the whole squeeze. With two fr tracks
          both narrow together at the same ratio, which lets the pair stay side by side much
          further down — stacking is held off to `sm` (640px). The summary's own `max-w-96`
          stops it running away on a wide window. */}
      {/* Stacks at 700px, not at a stock breakpoint, because the trigger is a measured one:
          with these 3fr/2fr tracks the Monthly/Yearly cards keep "Save 10%" on the same line as
          "Yearly" down to ~178px each, which is a 700px viewport. At 660px the badge wraps — so
          that is where the summary drops below and the left column takes the full width back. */}
      {/* One column, and no grid: the second track existed for the summary card, which now lives
          on `feature/jessica/sub-ui-cost-summary-parked`. */}
      <div>
        <div className="space-y-4">
          {!screensOnly && (
            <div
              role="radiogroup"
              aria-label={t('Billing period')}
              className="grid gap-4 sm:grid-cols-2"
            >
              {/* Yearly FIRST: it is the default selection and the one we recommend, so it takes
                  the leading slot rather than sitting second behind Monthly. */}
              {periodCard(
                BILLING_INTERVAL.YEARLY,
                t('Yearly'),
                rateLabel(yearlyMonthlyEquivalent),
                // yearlyPrice is the ANNUAL per-screen price, so this is the whole up-front charge
                // — NOT `yearlyMonthlyEquivalent`, which is that figure divided by twelve.
                billedLine(yearlyPrice * screenCount, true),
                true,
              )}
              {periodCard(
                BILLING_INTERVAL.MONTHLY,
                t('Monthly'),
                rateLabel(monthlyPrice),
                billedLine(monthlyPrice * screenCount, false),
              )}
            </div>
          )}

          {/* Flat (no shadow), unlike the Monthly/Yearly cards above it. Those are choices you
          click, and the shadow is what lifts them off the page as targets; this panel is a
          container you read and adjust in place, so it stays level with the surface. */}
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5">
                <h3
                  className={cn(
                    'font-semibold text-foreground',
                    BIG_COUNTER_BLOCK ? 'text-base' : 'text-sm',
                  )}
                >
                  {t('Number of screens')}
                </h3>
                {/* "Screen" is our word, not an obvious one: a first-time buyer has no way to
                    know it means one paired display and one license, or that the number is not
                    locked in. Own TooltipProvider because there is no global one in this app —
                    Radix's Tooltip.Root throws without an ancestor provider. */}
                {/* delayDuration={0}: Radix waits 700ms by default, which on a one-glyph hint
                    like this reads as nothing happening. */}
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label={t('What counts as a screen?')}
                        className="cursor-pointer text-muted-foreground transition-colors hover:text-foreground"
                        data-testid="wizard-screens-info"
                      >
                        <Info aria-hidden="true" className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      {t(
                        'Each screen running OptiSigns requires one license. Start with what you need and add more displays anytime.',
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {/* Used-vs-licensed, small and to the side. It was a `text-3xl` headline, which
                  made the number the loudest thing on the card even though it is not the thing
                  being decided — the stepper below is. It is context for that decision ("I run
                  9, am I buying enough?"), so it belongs where context goes: secondary type,
                  out of the way, still on the same line as the heading it qualifies.
                  Subscribers only, for the reason on the stepper card: a ratio against a
                  license count you have not bought yet reads as a problem to fix. */}
              {!hasNoActiveSubscription && (
                <div
                  className="shrink-0 text-right text-xs text-muted-foreground"
                  data-testid="wizard-screens-used"
                >
                  {/* Two flat counts, not "0/11". The denominator used to be `screenCount`,
                      which the stepper below is actively changing — so the ratio moved every
                      time the user pressed +, making it look like usage was changing when only
                      the license count they were shopping for was. These are two independent
                      facts, so they are stated independently: what is running now, and what is
                      paid for now.
                      One line with a dot between them rather than two stacked lines — at two
                      lines the pair read as a heading and a subheading, which implied the
                      second qualified the first. The dot is `aria-hidden`: it is punctuation
                      for the eye, and a screen reader announcing "middle dot" between two
                      numbers is noise. */}
                  <p>
                    {/* "paired", not "in use": the figure counts devices paired to the
                        account, which is a state the customer sets and can check against their
                        own screen list. "In use" invited a different reading — screens
                        currently playing something — which this number is not. */}
                    {usedScreens === 1
                      ? t('1 screen paired')
                      : t('{{screens}} screens paired', { screens: usedScreens })}
                    <span aria-hidden="true" className="mx-1.5">
                      &middot;
                    </span>
                    {/* "current", so the number is unmistakably what the account holds TODAY
                        rather than the total being bought — the stepper above is changing that
                        other number in real time, and "3 licenses" next to a counter reading 5
                        invites exactly the wrong reading. */}
                    {currentScreens === 1
                      ? t('1 current license')
                      : t('{{screens}} current licenses', { screens: currentScreens })}
                  </p>
                </div>
              )}
            </div>

            <div
              className={cn(
                'flex items-center gap-4',
                // Bigger type needs a bigger gap: at `mt-4` the enlarged stepper sat close
                // enough to the heading that the two read as one crowded block, which is what
                // made the card feel off even though the sizes themselves were right.
                BIG_COUNTER_BLOCK ? 'mt-6' : 'mt-4',
              )}
            >
              {/* `border-muted-foreground/40`, not the default `border` (#edf2f9) or even
                  `border-input` (#e7e9eb). Both are near-white blue-greys, and this control
                  sits on a white card, so at rest the stepper had no discernible edge — the
                  only thing marking it as a control was the two glyphs inside it. A step down
                  from `muted-foreground` (#6c757d) reads as an outline without turning into a
                  heavy box. */}
              {/* `icon-lg` on the buttons (h-10 = 40px), which is what sets this box's height —
                  one step up from `icon-md` so the whole control grows with the count rather than
                  the number growing inside a fixed frame. The size bump also takes the glyphs from
                  16px to 20px, keeping them proportionate to a 20px number. */}
              <div className="flex items-center rounded-md border border-muted-foreground/40">
                <Button
                  variant="ghost"
                  size={BIG_COUNTER_BLOCK ? 'icon-lg' : 'icon-sm'}
                  aria-label={t('Decrease screens')}
                  onClick={decrease}
                  data-testid="wizard-screens-decrease"
                >
                  <Minus />
                </Button>
                <ScreenCountInput
                  value={screenCount}
                  min={minScreens}
                  onCommit={onScreenCountChange}
                />
                <Button
                  variant="ghost"
                  size={BIG_COUNTER_BLOCK ? 'icon-lg' : 'icon-sm'}
                  aria-label={t('Increase screens')}
                  onClick={() => onScreenCountChange(screenCount + 1)}
                  data-testid="wizard-screens-increase"
                >
                  <Plus />
                </Button>
              </div>
              {/* Names the unit the stepper counts, so "3" is not a bare number. `text-base`
                  alongside the enlarged control — at `text-sm` it read as a caption on something
                  it is actually the label for. */}
              <p className="text-base text-foreground">{t('Screens')}</p>
              {/* Just the total. The per-screen rate used to be repeated here as
                  "x $27.00 / screen / month", but the billing-period cards directly above
                  already state it twice, once per cadence — so this row was restating the
                  multiplier next to its own product.
                  `ml-auto` pins the amount to the card's right edge, so the eye reads
                  count → unit → amount straight across. */}
              <p
                className={cn(
                  'ml-auto flex shrink-0 items-baseline gap-2',
                  BIG_COUNTER_BLOCK ? 'text-base' : 'text-sm',
                )}
              >
                {/* Yearly only: what the same screens would cost month-to-month over the same
                    span, struck through, so the annual discount is visible at the point of
                    choosing rather than only as a percentage on the card above. Guarded on a
                    real difference — if the two ever match, a struck-through equal number reads
                    as a rendering fault. */}
                {isYearly && screensRowWas > screensRowTotal && (
                  <span
                    // Same size as the amount it is struck against — the two numbers are the
                    // before/after of one figure, and a smaller "was" read as a footnote to
                    // the total rather than the other half of the comparison. Muting and the
                    // strike alone carry which one is live.
                    // `text-xl` with the live amount below: this row is the answer to what the
                    // chosen count costs, and at `text-lg` it sat a step under the counter beside
                    // it. Both halves move together — they are the before and after of ONE figure.
                    className={cn(
                      'text-muted-foreground line-through',
                      BIG_COUNTER_BLOCK && 'text-xl',
                    )}
                    data-testid="wizard-screens-total-was"
                  >
                    {totalMoney(screensRowWas)}
                  </span>
                )}
                <span
                  className={cn('font-semibold text-foreground', BIG_COUNTER_BLOCK && 'text-xl')}
                  data-testid="wizard-screens-total"
                >
                  {totalMoney(screensRowTotal)}
                </span>
              </p>
            </div>
          </div>

          {/* Payment, inline under the numbers so the whole purchase is one page. Renders the
              saved card as a single row when there is one, the Stripe fields when there is not. */}
          {/* Team-billing inputs — scio1 parity (Issue #49) */}
          {teamMode && (
            <div className="space-y-4 rounded-lg border bg-card p-4 shadow-sm">
              <div className="space-y-2">
                <Label htmlFor="wizard-team-name">{t('Team Name')}</Label>
                <Input
                  id="wizard-team-name"
                  value={teamName ?? ''}
                  onChange={(e) => onTeamNameChange?.(e.target.value)}
                  placeholder={t('Team Name')}
                  maxLength={64}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wizard-team-description">{t('Purpose')}</Label>
                <Textarea
                  id="wizard-team-description"
                  value={teamDescription ?? ''}
                  onChange={(e) => onTeamDescriptionChange?.(e.target.value)}
                  placeholder={t('Purpose')}
                  maxLength={256}
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* With no summary card, the note and the action it belongs to move here. The note keeps
              the column's `space-y-4` rhythm; the button gets `pt-2` on top of that so committing
              is not the same 16px step as everything else, and sits hard right — a full-width
          {/* The green annual-saving panel used to sit here. Removed: the Yearly card already
              carries a "Save 10%" badge and quotes both rates side by side, so the panel
              restated a saving the user had just chosen, in the one colour reserved for the
              CTA. */}
          {/* One row: the commit action stays hard right where it has always been, and Cancel
                Subscription sits opposite it on the left.
                `justify-end` with `mr-auto` on cancel rather than `justify-between`, so that a
                user with no cancel entry (a trial, a Free plan, team billing) still gets the
                primary button on the right instead of it sliding left as the row's only child.
                `pt-2` on top of the column's `space-y-4`: committing should not be the same 16px
                step as everything above it. */}
          <div className="flex items-center justify-end pt-2">
            {cancelSubscriptionAction('mr-auto')}
            {primaryCta()}
          </div>
        </div>
      </div>
    </div>
  );
}
